import { CircularProgress } from '@material-ui/core';
import React, { useState } from 'react';
import TourChecklist from './TourChecklist';

/**
 * Full-screen "pick your tours" screen for a guide who skipped or predates
 * onboarding. The list itself (search, city filter, select all) is
 * TourChecklist, shared with the onboarding Tours step; this adds the heading
 * and a fixed footer so Continue never scrolls away.
 */
const TourPicker = ({ tours, onSave, onSignOut }) => {
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    if (selected.size === 0 || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave([...selected]);
    } catch (e) {
      setError("We couldn't save your tours. Check your connection and try again.");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-50 flex flex-col"
      style={{ zIndex: 1300 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-picker-title"
      data-testid="tour-selection-gate">
      <div className="flex-1 overflow-y-auto">
        <div className="w-full max-w-[640px] mx-auto px-4 pt-10 pb-6">
          <h1 id="tour-picker-title" className="text-2xl md:text-3xl font-black text-slate-900 m-0">
            Which tours do you guide?
          </h1>
          <p className="text-base text-slate-600 leading-relaxed mt-2 mb-6 max-w-[56ch]">
            Pick the tours you lead so your tips and reviews reach you. You can change this later
            in your profile.
          </p>

          <TourChecklist tours={tours} selected={selected} onChange={setSelected} />
        </div>
      </div>

      <div className="border-0 border-t border-solid border-slate-200 bg-white">
        <div className="w-full max-w-[640px] mx-auto px-4 py-3 flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900" aria-live="polite">
              {selected.size === 0 ? 'No tours selected' : `${selected.size} selected`}
            </div>
            {error ? (
              <div className="text-xs text-red-700 mt-0.5" role="alert">
                {error}
              </div>
            ) : (
              <button
                type="button"
                onClick={onSignOut}
                className="bg-transparent border-0 p-0 text-xs text-slate-500 underline cursor-pointer">
                Sign out
              </button>
            )}
          </div>
          <button
            type="button"
            disabled={selected.size === 0 || saving}
            onClick={save}
            className="shrink-0 min-w-[140px] h-11 px-6 rounded-xl border-0 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white text-sm font-semibold cursor-pointer transition disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-default disabled:active:scale-100 flex items-center justify-center">
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourPicker;
